const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/PDFViewer/useVisiblePages.ts';
import { MutableRefObject, useEffect, useRef, useState } from 'react';

// Tolerance (px) for treating a scroll position as "at the top/bottom edge".
const SCROLL_EDGE_TOLERANCE_PX = 10;

export type UseVisiblePagesResult = {
    currentPage: number;
    visiblePageSet: Set<number>;
};

type Params = {
    enabled: boolean;
    pageCount: number;
    pageRefs: MutableRefObject<Map<number, HTMLDivElement>>;
    scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
    zoomWrapperRef: MutableRefObject<HTMLDivElement | null>;
    documentKey: number;
    buffer?: number;
};

export const useVisiblePages = ({
    enabled,
    pageCount,
    pageRefs,
    scrollContainerRef,
    zoomWrapperRef,
    documentKey,
    buffer = 2,
}: Params): UseVisiblePagesResult => {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const prevCenterRef = useRef(1);
    const prevSetKeyRef = useRef<string | null>(null);
    const [result, setResult] = useState<UseVisiblePagesResult>(() => ({
        currentPage: 1,
        visiblePageSet: buildPageSet(1, buffer, pageCount),
    }));

    useEffect(() => {
        if (!enabled || pageCount === 0) {
            observerRef.current?.disconnect();
            observerRef.current = null;
            return;
        }

        observerRef.current?.disconnect();

        const visibleHeights = new Map<number, number>();

        const observer = new IntersectionObserver(
            entries => {
                // Ignore measurements taken while a zoom-preview transform is applied
                if (zoomWrapperRef.current?.style.transform) return;

                entries.forEach(entry => {
                    const pageNum = parseInt((entry.target as HTMLElement).dataset.pageNumber ?? '0', 10);
                    if (pageNum > 0) {
                        // Remove pages that scrolled fully out of view so the map doesn't grow
                        // unbounded and stale zero-height entries can't win the "most visible" check.
                        if (!entry.isIntersecting) {
                            visibleHeights.delete(pageNum);
                        } else {
                            visibleHeights.set(pageNum, entry.intersectionRect.height);
                        }
                    }
                });

                if (visibleHeights.size === 0) return;

                let maxHeight = -1;
                let mostVisible = 1;
                visibleHeights.forEach((height, page) => {
                    if (height > maxHeight) {
                        maxHeight = height;
                        mostVisible = page;
                    }
                });

                // When scrolled fully to the top/bottom, pick the first/last page directly (with some margin)
                const scroller = scrollContainerRef.current;
                if (scroller) {
                    if (scroller.scrollTop <= SCROLL_EDGE_TOLERANCE_PX) {
                        mostVisible = 1;
                    } else if (
                        scroller.scrollTop >=
                        scroller.scrollHeight - scroller.clientHeight - SCROLL_EDGE_TOLERANCE_PX
                    ) {
                        mostVisible = pageCount;
                    }
                }

                const nextSet = buildPageSet(mostVisible, buffer, pageCount);
                const nextKey = [...nextSet].join(',');
                const centerChanged = mostVisible !== prevCenterRef.current;

                // Bail when neither the center nor the set membership changed, so we don't hand
                // out a fresh Set identity (→ wasted PDFDocument re-render) on every tick.
                if (!centerChanged && nextKey === prevSetKeyRef.current) return;
                prevCenterRef.current = mostVisible;
                prevSetKeyRef.current = nextKey;

                setResult({ currentPage: mostVisible, visiblePageSet: nextSet });
            },
            {
                root: scrollContainerRef.current,
                threshold: Array.from({ length: 11 }, (_, i) => i / 10),
            }
        );

        observerRef.current = observer;
        pageRefs.current.forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, [enabled, pageCount, documentKey, buffer]);

    return result;
};

const buildPageSet = (centerPage: number, buffer: number, pageCount: number): Set<number> => {
    const set = new Set<number>();
    const min = Math.max(1, centerPage - buffer);
    const max = Math.min(pageCount, centerPage + buffer);
    for (let p = min; p <= max; p++) {
        set.add(p);
    }
    return set;
};
